<template>
  <fs-pill v-bind="$attrs" circle-sides :color="parsedColor" :counter="counter" :tag="tag">
    <!-- A colour drawn with a picture — a print — shows it as a chip before its name, on a plain pill:
         an image under the text would make the name unreadable. -->
    <template v-if="swatchUrl" #pre>
      <img class="pill-swatch" draggable="false" :src="imagePath(swatchUrl, 48, 48, 'fill')" :alt="colorName" />
    </template>
    <slot>
      <fs-tooltip :text="tooltipText || colorName">
        {{ colorName }}
      </fs-tooltip>
    </slot>
  </fs-pill>
</template>

<script>
// A pre-defined FsPill for color pills
//
// Pros:
// * Consistency - Having it pre-defined ensures consistency.
// * Easier to use - By having a much smaller interface/API than the
//   full FsPill component, it's faster to get a grip of all relevant
//   properties (by not having non-relevant props here).
// * Easier to refactor - Want to change all color pills? All code in
//   one place.

import { convertRGBtoHEX, parseColor } from '@lib/colorManagement';
import { imagePath } from '@lib/imgproxy';

import FsPill from './fs-pill.vue';
import FsTooltip from '@components/poppers/fs-tooltip.vue';

export default {
  name: 'FsPillColor',
  props: {
    // String: The color of the pill, styled based on type (or, should be based on solid/outlined - see below)
    // Object: Style properties, fully managed (basically passed directly into the style-attribute)
    color: {
      type: [Object, String],
    },
    counter: {
      type: Boolean,
      default: false,
    },
    tooltipText: {
      type: String,
    },
    // FUTURE: If we need to support router-link, then look at fs-button on how to implement it
    tag: {
      type: String,
      default: 'span',
    },
    allowTransparent: {
      type: Boolean,
      default: true,
    },
  },

  components: { FsTooltip, FsPill },

  computed: {
    swatchUrl() {
      return typeof this.color === 'object' ? (this.color?.swatchUrl ?? null) : null;
    },

    parsedColor() {
      if (this.swatchUrl) return '#ffffff';

      const parsedColor = parseColor(this.color);
      if (parsedColor === 'transparent' && !this.allowTransparent) return '#fff';

      return parseColor(this.color);
    },

    colorName() {
      if (!this.color) return 'No color';

      return this.color?.name;
    },
  },

  methods: {
    convertRGBtoHEX,
    imagePath,
  },
};
</script>

<style scoped lang="scss">
@import '@styles/variables';

// Sized against the pill's own text, so it shrinks with compact rows and small pills.
.pill-swatch {
  flex: none;
  width: 1.5em;
  height: 1.5em;
  margin: 0 0.4em 0 -0.2em;
  border-radius: 0.3em;
  object-fit: cover;
}

.pill--counter[data-specificity-hack] {
  background-color: $c-bg-light;
  border-color: $c-dark;
  color: $c-dark;
}
</style>
